import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as platformService from "../../services/platformService";
import { notifyModuleAccessChanged } from "../../hooks/useModuleAccess";
import ApprovalDrawer from "./ApprovalDrawer";
import { EmployeeSelect, EmptyState, Field, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, Tabs, cardStyle, formatDate, formatDateTime, iconBtnStyle, inputStyle, pageStyle, statusBadge, statusLabel, tdStyle, thStyle, todayYmd, useLoad } from "../../components/ui";

// One inbox for everything a person has to decide - HR requests (leave, letters ...)
// next to Finance and Dealership requests (discounts, credit limits, journals ...) -
// what they decided, what they asked for, and who takes over while they are away.
// Each row says where it came from; a decision goes back to that module's rules.
const TAB = { TODO: "todo", DONE: "done", MINE: "mine", DELEGATE: "delegate" };
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };
const rowKey = (r) => `${r.source}:${r.request_id}`;
const sourceLabel = (r) => (r.source === "HR" ? "HR" : String(r.request_type || "").startsWith("FIN.") ? "Finance" : "Dealership");

function RequestTable({ rows, selectable, selected, onToggle, onOpen, columns }) {
  return (
    <div style={{ ...cardStyle, overflowX: "auto" }}>
      <table className="table mb-0" style={{ minWidth: 680 }}>
        <thead>
          <tr>
            {selectable ? <th style={thStyle} /> : null}
            {columns.map((c) => (
              <th key={c.label} style={thStyle}>
                {c.label}
              </th>
            ))}
            <th style={thStyle} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={rowKey(r)}>
              {selectable ? (
                <td style={tdStyle}>
                  <input type="checkbox" aria-label="Select request" checked={selected.includes(rowKey(r))} onChange={() => onToggle(rowKey(r))} />
                </td>
              ) : null}
              {columns.map((c) => (
                <td key={c.label} style={tdStyle}>
                  {c.render(r)}
                </td>
              ))}
              <td style={{ ...tdStyle, textAlign: "right" }}>
                <button type="button" style={linkBtn} onClick={() => onOpen({ source: r.source, request_id: r.request_id })}>
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const typeCell = (r) => (
  <>
    <div>{r.request_type_label}</div>
    <div style={{ fontSize: 12, color: "#6B7280" }}>{sourceLabel(r)}</div>
  </>
);

function Inbox({ history, onOpen, refreshKey, onChanged }) {
  const { data, loading, error, reload } = useLoad(() => platformService.getInbox(history), [history, refreshKey]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const rows = data || [];

  useEffect(() => setSelected([]), [data]);
  const toggle = (key) => setSelected((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));

  const approveSelected = async () => {
    setBusy(true);
    try {
      const items = rows.filter((r) => selected.includes(rowKey(r))).map((r) => ({ source: r.source, request_id: r.request_id }));
      const res = await platformService.bulkApprovalAction(items, "APPROVE");
      if (res?.success) {
        const failed = (res.data || []).filter((r) => !r.success);
        failed.length ? toast.warn(`${res.message}. ${failed[0].message}`) : toast.success(res.message);
        onChanged();
      } else toast.error(platformService.responseMessage(res));
    } finally {
      setBusy(false);
    }
  };

  const columns = history
    ? [
        { label: "Raised by", render: (r) => r.requester_name || "—" },
        { label: "Request", render: (r) => r.summary || r.request_type_label },
        { label: "Type", render: typeCell },
        { label: "My decision", render: (r) => <span style={statusBadge(r.my_status)}>{statusLabel(r.my_status)}</span> },
        { label: "Decided", render: (r) => formatDateTime(r.acted_at) },
      ]
    : [
        { label: "Raised by", render: (r) => r.requester_name || "—" },
        { label: "Request", render: (r) => r.summary || r.request_type_label },
        { label: "Type", render: typeCell },
        { label: "Submitted", render: (r) => formatDateTime(r.submitted_at) },
        { label: "Due", render: (r) => formatDateTime(r.due_at) },
      ];

  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      {rows.length === 0 ? (
        <EmptyState title={history ? "Nothing decided yet" : "You are all caught up"} text={history ? "Requests you approve or reject appear here." : "Requests waiting for your decision appear here."} />
      ) : (
        <>
          {!history && selected.length > 0 ? (
            <div className="d-flex align-items-center gap-2 mb-2">
              <span style={{ fontSize: 13 }}>{selected.length} selected</span>
              <PrimaryButton onClick={approveSelected} disabled={busy}>
                Approve selected
              </PrimaryButton>
            </div>
          ) : null}
          <RequestTable rows={rows} selectable={!history} selected={selected} onToggle={toggle} onOpen={onOpen} columns={columns} />
        </>
      )}
    </LoadState>
  );
}

function MyRequests({ onOpen, refreshKey }) {
  const { data, loading, error, reload } = useLoad(() => platformService.getMyRequests(), [refreshKey]);
  const rows = data || [];
  const columns = [
    { label: "Request", render: (r) => r.summary || r.request_type_label },
    { label: "Type", render: typeCell },
    { label: "Status", render: (r) => <span style={statusBadge(r.status)}>{statusLabel(r.status)}</span> },
    { label: "Step", render: (r) => (r.status === "PENDING" ? `${r.current_level} of ${r.total_levels}` : "—") },
    { label: "Submitted", render: (r) => formatDateTime(r.submitted_at) },
  ];
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      {rows.length === 0 ? <EmptyState title="No requests yet" text="Requests you raise in HR, Finance or the Dealership appear here." /> : <RequestTable rows={rows} onOpen={onOpen} columns={columns} />}
    </LoadState>
  );
}

function DelegationForm({ show, onHide, onSaved }) {
  const [delegate, setDelegate] = useState(null);
  const [from, setFrom] = useState(todayYmd());
  const [to, setTo] = useState(todayYmd());
  const [onlyOnLeave, setOnlyOnLeave] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!delegate) return toast.error("Choose who takes over your approvals");
    setSaving(true);
    try {
      const res = await platformService.createDelegation({ delegate_id: delegate, from_date: from, to_date: to, only_when_on_leave: onlyOnLeave });
      if (res?.success) {
        toast.success(res.message || "Saved");
        setDelegate(null);
        onSaved();
      } else toast.error(platformService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title="Hand over my approvals"
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>Covers every kind of approval: HR, Finance and the Dealership.</div>
      <Field label="Who takes over" required>
        <EmployeeSelect value={delegate} onChange={setDelegate} />
      </Field>
      <div className="row">
        <div className="col-6">
          <Field label="From" required htmlFor="dlg-from">
            <input id="dlg-from" type="date" className="form-control" style={inputStyle} value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
        </div>
        <div className="col-6">
          <Field label="To" required htmlFor="dlg-to">
            <input id="dlg-to" type="date" className="form-control" style={inputStyle} value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </div>
      <div className="form-check">
        <input id="dlg-only-on-leave" type="checkbox" className="form-check-input" checked={onlyOnLeave} onChange={(e) => setOnlyOnLeave(e.target.checked)} />
        <label htmlFor="dlg-only-on-leave" className="form-check-label" style={{ fontSize: 14 }}>
          Only while I am on approved leave
        </label>
      </div>
    </SlidePanel>
  );
}

function Delegations() {
  const { data, loading, error, reload } = useLoad(() => platformService.listDelegations(), []);
  const [adding, setAdding] = useState(false);
  const rows = data || [];

  const cancel = async (id) => {
    const res = await platformService.cancelDelegation(id).catch((e) => ({ success: false, message: e.message }));
    if (res?.success) {
      toast.success(res.message || "Cancelled");
      reload();
    } else toast.error(platformService.responseMessage(res));
  };

  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setAdding(true)}>Hand over my approvals</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No hand-overs" text="Set one up before you go on leave so requests are not held up." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0">
              <thead>
                <tr>
                  {["From", "To", "Dates", "When", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td style={tdStyle}>{d.delegator_name || "Account owner"}</td>
                    <td style={tdStyle}>{d.delegate_name}</td>
                    <td style={tdStyle}>
                      {formatDate(d.from_date)} – {formatDate(d.to_date)}
                    </td>
                    <td style={tdStyle}>{d.only_when_on_leave ? "Only while on leave" : "Always"}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <button type="button" style={linkBtn} onClick={() => cancel(d.id)}>
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <DelegationForm
        show={adding}
        onHide={() => setAdding(false)}
        onSaved={() => {
          setAdding(false);
          reload();
        }}
      />
    </>
  );
}

export default function Approvals() {
  const [tab, setTab] = useState(TAB.TODO);
  const [open, setOpen] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pending, setPending] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
    notifyModuleAccessChanged(); // the menu's pending count
  }, []);

  useEffect(() => {
    let alive = true;
    platformService
      .getApprovalCount()
      .then((res) => alive && setPending(Number(res?.data?.pending) || 0))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  return (
    <div style={pageStyle}>
      <PageHeader title="Approvals" subtitle="Everything waiting for your decision - HR, Finance and Dealership - and the requests you have made" />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: TAB.TODO, label: "To approve", badge: pending || null },
          { key: TAB.DONE, label: "Decided" },
          { key: TAB.MINE, label: "My requests" },
          { key: TAB.DELEGATE, label: "Hand-overs" },
        ]}
      />
      {tab === TAB.TODO ? <Inbox history={false} onOpen={setOpen} refreshKey={refreshKey} onChanged={refresh} /> : null}
      {tab === TAB.DONE ? <Inbox history onOpen={setOpen} refreshKey={refreshKey} onChanged={refresh} /> : null}
      {tab === TAB.MINE ? <MyRequests onOpen={setOpen} refreshKey={refreshKey} /> : null}
      {tab === TAB.DELEGATE ? <Delegations /> : null}
      {open ? <ApprovalDrawer item={open} onClose={() => setOpen(null)} onChanged={refresh} /> : null}
    </div>
  );
}
